import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../../lib/cn.js'

const DEFAULT_DELAY_MS = 300

/**
 * Hover tooltip for suggestion card title/description.
 *
 * Plain mouseenter/leave + fixed portal into `.widget-root` (shadow DOM).
 * Radix Tooltip was unreliable against the panel's max z-index and
 * overflow-hidden ancestors.
 */
export function SuggestionCardTooltip({
  title,
  description,
  disabled = false,
  delayMs = DEFAULT_DELAY_MS,
  children,
  className,
}) {
  const triggerRef = useRef(null)
  const delayTimerRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const [portalTarget, setPortalTarget] = useState(null)

  const plainTitle = typeof title === 'string' ? title.trim() : ''
  const plainDescription =
    typeof description === 'string' ? description.trim() : ''
  const canShow = !disabled && Boolean(plainTitle || plainDescription)

  const clearDelay = () => {
    if (delayTimerRef.current != null) {
      window.clearTimeout(delayTimerRef.current)
      delayTimerRef.current = null
    }
  }

  useEffect(() => () => clearDelay(), [])

  useLayoutEffect(() => {
    const el = triggerRef.current
    if (!el) {
      setPortalTarget(null)
      return
    }
    // Prefer `.widget-root` so theme CSS variables still apply.
    const widgetRoot = el.closest('.widget-root')
    if (widgetRoot instanceof HTMLElement) {
      setPortalTarget(widgetRoot)
      return
    }
    const root = el.getRootNode()
    setPortalTarget(root instanceof ShadowRoot ? root : document.body)
  }, [canShow])

  const updatePosition = () => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setCoords({
      top: rect.top,
      left: rect.left + rect.width / 2,
    })
  }

  const handleEnter = () => {
    if (!canShow) return
    clearDelay()
    const wait = Math.max(0, Number(delayMs) || 0)
    delayTimerRef.current = window.setTimeout(() => {
      delayTimerRef.current = null
      updatePosition()
      setOpen(true)
    }, wait)
  }

  const handleLeave = () => {
    clearDelay()
    setOpen(false)
  }

  if (!canShow) {
    return <div className={className}>{children}</div>
  }

  const tooltip =
    open && coords && portalTarget
      ? createPortal(
          <div
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top - 8,
              left: coords.left,
              transform: 'translate(-50%, -100%)',
              zIndex: 2147483647,
              // Fallbacks if theme vars are missing on the portal host.
              backgroundColor: 'var(--color-surface, #ffffff)',
              color: 'var(--color-surface-foreground, #212529)',
              borderColor: 'var(--color-border, #dee2e6)',
            }}
            className={cn(
              'pointer-events-none w-max max-w-[240px] rounded-md border px-2.5 py-2 text-left shadow-md',
              'text-[12px] leading-snug',
            )}
          >
            {plainTitle ? (
              <div className="font-semibold whitespace-pre-wrap break-words">
                {plainTitle}
              </div>
            ) : null}
            {plainDescription ? (
              <div
                className={cn(
                  'whitespace-pre-wrap break-words',
                  plainTitle && 'mt-1',
                )}
                style={{
                  color: 'var(--color-muted-foreground, #6c757d)',
                }}
              >
                {plainDescription}
              </div>
            ) : null}
          </div>,
          portalTarget,
        )
      : null

  return (
    <div
      ref={triggerRef}
      className={cn('min-w-0', className)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
      {tooltip}
    </div>
  )
}
